import { AssetsForms } from '@/entities/assets';
import { BeneficiaryCreateForm } from '@/entities/beneficiary/helpers';
import { ContactsForms } from '@/entities/contact';
import { getPersonalIdentifierForms } from '@/entities/personalIdentifiers/helpers/get-forms';
import { PrivateDocumentForm } from '@/shared/lib/forms/documents/private';

import { OmittedBaseDto, TDtoWrapper } from '../forms/form-wrapper.types';
import { PersonalIdentifierType } from '../sj-orm';
import { DocumentType } from '../sj-orm/models/document/document-base.dto';
import { QuestionType } from '../sj-orm/models/form/question.dto';
import { CollectionName } from '../sj-orm/types';

import { FAST_ADD_COMMON_ITEM } from './useFormRenderV2/constants';

const BENEFICIARIES_FORM_TYPE = 'beneficiaries';

export const FormsByCollection = {
  [CollectionName.ASSETS]: AssetsForms,
  [CollectionName.CONTACTS]: ContactsForms,
  [CollectionName.PRIVATE_DOCUMENTS]: PrivateDocumentForm,
  [CollectionName.BENEFICIARIES]: BeneficiaryCreateForm,
  [CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS]: {},
} satisfies Partial<Record<CollectionName, unknown>>;

export type PartialFormAllowedCollection = keyof typeof FormsByCollection;

type TFormType = TDtoWrapper<OmittedBaseDto, { [TK in keyof OmittedBaseDto]: QuestionType }>;

const CommonForms: Record<string, TFormType> = {
  [CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS]: [] as TFormType,
};

export const isAllowedCollection = (value: unknown): value is PartialFormAllowedCollection => {
  return (
    typeof value === 'string' &&
    (value === CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS ||
      value === CollectionName.PRIVATE_DOCUMENTS ||
      value === CollectionName.ASSETS ||
      value === CollectionName.CONTACTS ||
      value === CollectionName.BENEFICIARIES)
  );
};

const getCollectionForm = (collection: PartialFormAllowedCollection, type: string) => {
  const forms = FormsByCollection[collection] as
    | Record<string, TFormType | TFormType[] | undefined>
    | undefined;

  if (!forms) {
    return null;
  }

  const collectionForm =
    collection === CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS
      ? getPersonalIdentifierForms(type as PersonalIdentifierType)
      : collection === CollectionName.BENEFICIARIES
      ? forms[BENEFICIARIES_FORM_TYPE]
      : collection === CollectionName.PRIVATE_DOCUMENTS
      ? forms[DocumentType.PRIVATE_DOCUMENTS]
      : forms[type];

  const form = type === FAST_ADD_COMMON_ITEM ? CommonForms[collection] : collectionForm;
  const formForMap = Array.isArray(form) ? form[0] : form;

  return formForMap;
};

type GetPartialFormProps = {
  collectionName: PartialFormAllowedCollection;
  type: string;
  withRequiredFields: boolean;
  withFastAddFields: boolean;
  customFields?: string[];
};

export const getPartialForm = ({
  collectionName,
  type,
  withFastAddFields,
  withRequiredFields,
  customFields = [],
}: GetPartialFormProps) => {
  const formForMap = getCollectionForm(collectionName, type);
  if (!formForMap) {
    return null;
  }

  const resultForm: TFormType = {} as TFormType;
  for (const [key, val] of Object.entries(formForMap)) {
    if (withRequiredFields && val.required) {
      resultForm[key as keyof TFormType] = val;
    }

    if (withFastAddFields && val.showInFastAdd) {
      resultForm[key as keyof TFormType] = val;
    }

    if (customFields.includes(key)) {
      resultForm[key as keyof TFormType] = val;
    }
  }

  return resultForm;
};
